import {
  API_ROUTES,
  calibrationSchema,
  intelligenceSettingsSchema,
  recordInsightsSchema,
  type UpdateIntelligenceSettings,
  vendorIntelligenceSchema,
} from '@hvac/contracts';
import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';
import { apiClient } from './queries.js';

/** Intelligence queries. */

export const intelligenceKeys = {
  all: (organizationId: string) => ['intelligence', organizationId] as const,
  record: (organizationId: string, recordId: string) =>
    ['intelligence', organizationId, 'record', recordId] as const,
  vendor: (organizationId: string, vendorId: string) =>
    ['intelligence', organizationId, 'vendor', vendorId] as const,
} as const;

export const recordInsightsQuery = (organizationId: string, recordId: string) =>
  queryOptions({
    queryKey: intelligenceKeys.record(organizationId, recordId),
    queryFn: ({ signal }) =>
      apiClient.request({
        path: API_ROUTES.intelligence.record(organizationId, recordId),
        schema: recordInsightsSchema,
        signal,
      }),
  });

export const vendorIntelligenceQuery = (organizationId: string, vendorId: string) =>
  queryOptions({
    queryKey: intelligenceKeys.vendor(organizationId, vendorId),
    queryFn: ({ signal }) =>
      apiClient.request({
        path: API_ROUTES.intelligence.vendor(organizationId, vendorId),
        schema: vendorIntelligenceSchema,
        signal,
      }),
  });

export const intelligenceSettingsQuery = (organizationId: string) =>
  queryOptions({
    queryKey: ['intelligence', organizationId, 'settings'] as const,
    queryFn: ({ signal }) =>
      apiClient.request({
        path: API_ROUTES.intelligence.settings(organizationId),
        schema: intelligenceSettingsSchema,
        signal,
      }),
    staleTime: 300_000,
  });

export const calibrationQuery = (organizationId: string) =>
  queryOptions({
    queryKey: ['intelligence', organizationId, 'calibration'] as const,
    queryFn: ({ signal }) =>
      apiClient.request({
        path: API_ROUTES.intelligence.calibration(organizationId),
        schema: calibrationSchema,
        signal,
      }),
    staleTime: 60_000,
  });

export async function updateIntelligenceSettings(
  organizationId: string,
  body: UpdateIntelligenceSettings,
) {
  return apiClient.request({
    method: 'PATCH',
    path: API_ROUTES.intelligence.settings(organizationId),
    schema: intelligenceSettingsSchema,
    body,
  });
}

export async function rebuildIntelligence(organizationId: string) {
  return apiClient.request({
    method: 'POST',
    path: API_ROUTES.intelligence.rebuild(organizationId),
    schema: z.object({ vendors: z.number().int() }),
    body: {},
  });
}
